import { useEffect, useRef, useState } from 'react'
import { ChevronsUpDownIcon, Loader2Icon, XIcon } from 'lucide-react'
import { defaultFilter } from 'cmdk'

import { Button } from '@/components/ui/button'
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from '@/components/ui/command'
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import { m } from '@/lib/paraglide/messages'

export interface ComboboxOption {
    value: string
    /** Vừa là chữ mặc định trên nút, vừa là từ khoá gõ để tìm (xem `keywords`
     *  bên dưới). Muốn tách hai việc đó thì dùng thêm `triggerLabel`. */
    label: string
    /** Optional custom render cho dropdown row. Nếu không truyền,
     *  fallback text `<span>{label}</span>`. */
    render?: () => React.ReactNode
    /**
     * Chữ hiện trên NÚT sau khi chọn, khi nó cần khác `label`.
     *
     * Sinh ra cho các cuốn sổ (#M-16): ở đó mã lô bị giấu khỏi mắt người ghi sổ
     * nhưng vẫn phải gõ mã tìm được, nên `label` giữ mã (để tìm) còn nút chỉ
     * hiện loại chè và ngày giờ.
     */
    triggerLabel?: string
    /** Tiêu đề nhóm. Các option LIỀN NHAU cùng `group` gom vào một nhóm có
     *  tiêu đề (vd "Gợi ý tương đồng"). Không truyền → một nhóm không tiêu đề
     *  như cũ. */
    group?: string
}

/** Gom các option liền nhau cùng `group` thành từng đoạn để render tiêu đề. */
function groupOptions(
    options: Array<ComboboxOption>,
): Array<{ heading?: string; options: Array<ComboboxOption> }> {
    const sections: Array<{
        heading?: string
        options: Array<ComboboxOption>
    }> = []
    for (const opt of options) {
        const last = sections.at(-1)
        if (last && last.heading === opt.group) last.options.push(opt)
        else sections.push({ heading: opt.group, options: [opt] })
    }
    return sections
}

interface ComboboxProps {
    options: Array<ComboboxOption>
    value?: string
    onChange: (value: string) => void
    onSearchChange?: (search: string) => void
    /** false → không filter client-side, trust server (dùng với onSearchChange). */
    shouldFilter?: boolean
    placeholder?: string
    searchPlaceholder?: string
    emptyText?: string
    disabled?: boolean
    loading?: boolean
    className?: string
    /** Show a clear (×) button when a value is selected, resetting to ''.
     *  Off by default so existing single-select callers are unaffected. */
    allowClear?: boolean
    /** Accessible label for the clear button (defaults to Vietnamese). */
    clearLabel?: string
    /** Gọi khi người dùng cuộn tới cuối danh sách. Không truyền → không có
     *  infinite scroll, mọi call site cũ giữ nguyên hành vi. */
    onLoadMore?: () => void
    /** Còn trang sau để tải. Chỉ có ý nghĩa khi truyền `onLoadMore`. */
    hasMore?: boolean
    /** Đang tải trang tiếp — hiện spinner ở cuối danh sách. */
    loadingMore?: boolean
    /** Bắn ra mỗi lần dropdown mở/đóng. Ô tìm kiếm nằm trong popover và bị Radix
     *  unmount khi đóng, nên caller nào giữ state tìm kiếm phải reset ở đây —
     *  không thì ô quay lại rỗng mà danh sách vẫn bị lọc theo từ khoá cũ. */
    onOpenChange?: (open: boolean) => void
    /** Id gắn vào nút mở dropdown, để `<label htmlFor>` trỏ đúng phần tử focus
     *  được. Optional nên mọi call-site cũ giữ nguyên hành vi. */
    id?: string
    /** Tên đọc được của nút mở dropdown khi không có `<label>` trỏ vào nó. */
    ariaLabel?: string
    /** Đánh dấu ô đang lỗi cho trình đọc màn hình. */
    invalid?: boolean
    /** Nguồn nhãn cho `value` không nằm trong `options` — không tránh được với
     *  danh sách phân trang (sửa bản ghi cũ của hộ trồng đã bị xoá mềm). Thiếu
     *  nó thì trigger tụt về placeholder và trông như đang rỗng. `options` được
     *  ưu tiên khi giá trị có mặt ở đó. */
    selectedOption?: ComboboxOption
}

export function Combobox({
    id,
    ariaLabel,
    invalid,
    options,
    value,
    onChange,
    onSearchChange,
    shouldFilter,
    placeholder,
    searchPlaceholder,
    emptyText,
    disabled,
    loading,
    className,
    allowClear,
    clearLabel,
    onLoadMore,
    hasMore,
    loadingMore,
    onOpenChange,
    selectedOption,
}: ComboboxProps) {
    const [open, setOpenState] = useState(false)
    // Mọi đường đóng/mở phải đi qua đây: chọn một dòng gọi thẳng setOpen(false),
    // không qua Popover, nên nếu chỉ gắn onOpenChange vào Popover thì caller
    // không bao giờ biết picker đã đóng sau khi chọn.
    const setOpen = (next: boolean) => {
        setOpenState(next)
        onOpenChange?.(next)
    }
    const inList = options.find((o) => o.value === value)
    const selected =
        inList ??
        (value && selectedOption?.value === value ? selectedOption : undefined)
    const showClear = allowClear && !!value && !disabled && !loading

    // Sentinel ở cuối danh sách: lọt vào khung nhìn thì xin trang tiếp.
    //
    // Node được giữ bằng state chứ KHÔNG phải useRef: nội dung popover nằm
    // trong portal và mount ở commit sau commit đổi `open`, nên effect đọc ref
    // sẽ thấy null rồi không bao giờ chạy lại — observer chẳng bao giờ được gắn.
    // Setter của useState có identity cố định nên dùng thẳng làm callback ref.
    const [listEl, setListEl] = useState<HTMLDivElement | null>(null)
    const [sentinelEl, setSentinelEl] = useState<HTMLDivElement | null>(null)

    // Ba cờ load-more đọc qua ref, KHÔNG đưa vào deps: cả ba đổi liên tục
    // (onLoadMore thường là arrow mới mỗi render), mà mỗi lần effect chạy lại là
    // một observer mới — trình duyệt bắn callback ngay tại observe(), nên nhánh
    // tải thêm bị đánh thức ở mọi lần render và chỉ còn cờ loadingMore chặn lại.
    const loadMoreState = useRef({ onLoadMore, hasMore, loadingMore })
    loadMoreState.current = { onLoadMore, hasMore, loadingMore }

    const loadMoreArmed = Boolean(onLoadMore)

    useEffect(() => {
        if (!loadMoreArmed || !sentinelEl) return
        const observer = new IntersectionObserver(
            (entries) => {
                const st = loadMoreState.current
                if (
                    entries.some((e) => e.isIntersecting) &&
                    st.hasMore &&
                    !st.loadingMore
                ) {
                    st.onLoadMore?.()
                }
            },
            // rootMargin: nạp trước khi sentinel thực sự chạm đáy, cuộn nhanh
            // không bị khựng.
            { root: listEl, rootMargin: '120px' },
        )
        observer.observe(sentinelEl)
        return () => observer.disconnect()
    }, [loadMoreArmed, sentinelEl, listEl])

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <div className={cn('relative w-full min-w-0', className)}>
                <PopoverTrigger asChild>
                    <Button
                        id={id}
                        type="button"
                        variant="outline"
                        role="combobox"
                        aria-expanded={open}
                        aria-label={ariaLabel}
                        aria-invalid={invalid || undefined}
                        disabled={disabled || loading}
                        className={cn(
                            'w-full min-w-0 justify-between font-normal h-11 md:h-9',
                            !selected && 'text-muted-foreground',
                        )}
                    >
                        <span className="min-w-0 flex-1 truncate text-left">
                            {selected
                                ? (selected.triggerLabel ?? selected.label)
                                : placeholder}
                        </span>
                        {loading ? (
                            <Loader2Icon className="ml-2 size-4 shrink-0 animate-spin opacity-50" />
                        ) : showClear ? (
                            // Reserve the icon slot so the label truncates the
                            // same as with the chevron; the real clickable clear
                            // button is the overlay below.
                            <span
                                className="ml-2 size-4 shrink-0"
                                aria-hidden
                            />
                        ) : (
                            <ChevronsUpDownIcon className="ml-2 size-4 shrink-0 opacity-50" />
                        )}
                    </Button>
                </PopoverTrigger>
                {showClear && (
                    <button
                        type="button"
                        aria-label={clearLabel ?? m.combobox_clear()}
                        onClick={(e) => {
                            e.stopPropagation()
                            onChange('')
                        }}
                        className="absolute right-2 top-1/2 -translate-y-1/2 rounded-sm p-1 text-muted-foreground hover:text-foreground"
                    >
                        <XIcon className="size-4" />
                    </button>
                )}
            </div>
            <PopoverContent
                // Mobile (< md): rộng 90vw — ô chọn trên điện thoại thường hẹp
                // (lưới 2 cột, dòng hàng) nên bó theo ô thì không đọc nổi tên
                // lô/sản phẩm. Desktop giữ rộng bằng ô, sàn 400px, trần 90vw.
                className="w-[90vw] max-w-[90vw] p-0 sm:min-w-[400px] md:w-[var(--radix-popover-trigger-width)]"
                align="start"
            >
                <Command
                    label={ariaLabel}
                    shouldFilter={shouldFilter ?? true}
                    // cmdk chấm điểm trên `value + " " + keywords`. Value giờ là
                    // id nên nếu để mặc định thì uuid lọt vào vùng tìm kiếm: gõ
                    // một ký tự hex (chữ số, a–f) là khớp gần hết danh sách.
                    // Chấm điểm riêng trên label để giữ đúng hành vi cũ.
                    filter={(_value, search, keywords) =>
                        defaultFilter?.(keywords?.join(' ') ?? '', search) ?? 0
                    }
                >
                    <CommandInput
                        placeholder={searchPlaceholder}
                        onValueChange={onSearchChange}
                    />
                    <CommandList ref={setListEl}>
                        <CommandEmpty>{emptyText}</CommandEmpty>
                        {groupOptions(options).map((section, idx) => (
                            <CommandGroup
                                key={`${idx}-${section.heading ?? ''}`}
                                heading={section.heading}
                            >
                                {section.options.map((opt) => (
                                    <CommandItem
                                        key={opt.value}
                                        // cmdk định danh item bằng `value`. Dùng
                                        // label ở đây thì hai bản ghi trùng tên
                                        // (VD hai tài khoản của cùng một người) bị
                                        // coi là một dòng: cả hai cùng sáng và bàn
                                        // phím không tách được. Value là id duy
                                        // nhất, còn tìm kiếm theo tên vẫn chạy nhờ
                                        // `keywords`.
                                        value={opt.value}
                                        keywords={[opt.label]}
                                        onSelect={() => {
                                            onChange(opt.value)
                                            setOpen(false)
                                        }}
                                        className={cn(
                                            // Persistent tint for the picked option so
                                            // it's distinguishable from cmdk's hover /
                                            // keyboard "data-selected" highlight, which
                                            // also uses bg-accent and would otherwise
                                            // merge into the picked row.
                                            value === opt.value &&
                                                'bg-primary/10 font-medium text-primary',
                                        )}
                                    >
                                        {opt.render ? (
                                            opt.render()
                                        ) : (
                                            <span className="truncate">
                                                {opt.label}
                                            </span>
                                        )}
                                    </CommandItem>
                                ))}
                            </CommandGroup>
                        ))}
                        {onLoadMore && (
                            // Nằm ngoài CommandGroup: cmdk chỉ coi CommandItem
                            // là lựa chọn được, div này không lọt vào điều hướng
                            // bàn phím.
                            <div
                                ref={setSentinelEl}
                                className="flex h-8 items-center justify-center"
                            >
                                {loadingMore && (
                                    <Loader2Icon className="size-4 animate-spin opacity-50" />
                                )}
                            </div>
                        )}
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    )
}
